import { useNavigation } from 'app/hooks/navigation';
import { ListingType, type Listing, type UpdateListingDTO } from 'app/types/listing';
import equal from 'fast-deep-equal';
import { useTranslation } from 'react-i18next';
import Toast from 'react-native-toast-message';

export function isCopyOnlyListingEdit(values: Record<string, unknown>, initialValues?: Record<string, unknown>) {
  if (!initialValues || Number(values.duplicates) <= 0 || !Number.isFinite(Number(values.duplicates))) return false;

  // Compare values because category/address pickers also update fields without marking them dirty.
  return Object.entries(initialValues).every(([field, value]) => field === 'duplicates' || equal(values[field], value));
}

export function useListingSaveFeedback() {
  const navigation = useNavigation();
  const { t } = useTranslation('one-listing');

  const showError = (message: string) => {
    Toast.show({
      type: 'error',
      position: 'bottom',
      bottomOffset: 100,
      text1: t('saveToast.errorTitle'),
      text2: message,
      visibilityTime: 6000,
    });
  };

  const onSuccess = (listing: Listing, variables: UpdateListingDTO, copyOnly: boolean) => {
    const copiesCreated = listing.type === ListingType.offer ? (variables.duplicates ?? 0) : 0;

    if (copiesCreated > 0 && copyOnly) {
      navigation.navigate(
        'main',
        {
          screen: 'management',
          params: {
            screen: 'my-listings',
            params: { type: ListingType.offer },
          },
        },
        { pop: true },
      );
    } else {
      navigation.goBack();
    }

    // Show on the destination screen after the edit modal has closed.
    setTimeout(() => {
      Toast.show({
        type: 'success',
        position: 'bottom',
        bottomOffset: 100,
        text1: t('saveToast.title'),
        text2: copiesCreated > 0 ? t('saveToast.copies', { count: copiesCreated }) : t('saveToast.text'),
        visibilityTime: 5000,
      });
    }, 400);
  };

  const onError = (error: Error) => {
    showError(t(error.message, { ns: 'api-errors', defaultValue: t('saveToast.errorText') }));
  };

  const onInvalid = () => showError(t('invalidFormError'));

  return { onSuccess, onError, onInvalid };
}
